import { useState, useEffect, useCallback } from 'react';
import { useAuthStore }    from '../store/authStore';
import { useToast }        from '../components/Toast';

// ── Confirm Modal ──────────────────────────────────────────────────────────
function ConfirmModal({ isOpen, message, onConfirm, onCancel }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
      <div className="glass-strong rounded-2xl p-6 max-w-sm w-full space-y-4 animate-slide-up">
        <p className="text-white text-sm">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2 rounded-lg text-sm text-white/60 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.05)' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2 rounded-lg text-sm font-semibold text-amber-300"
            style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)' }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Section heading ────────────────────────────────────────────────────────
function SectionHead({ children }) {
  return (
    <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
      {children}
    </h2>
  );
}

// ── Member row (requests panel) ────────────────────────────────────────────
function RequestRow({ req, onApprove, onReject }) {
  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-lg"
      style={{ background: 'rgba(255,255,255,0.04)' }}>
      <div className="flex-1">
        <p className="text-white text-sm font-medium">{req.display_name ?? req.displayName}</p>
        <p className="text-white/40 text-xs">{req.email}</p>
      </div>
      <button
        onClick={() => onApprove(req.id)}
        className="px-3 py-1 rounded-lg text-xs font-semibold text-green-300 transition-all"
        style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)' }}
      >
        Approve
      </button>
      <button
        onClick={() => onReject(req.id)}
        className="px-3 py-1 rounded-lg text-xs font-semibold text-red-300 transition-all"
        style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}
      >
        Reject
      </button>
    </div>
  );
}

// ── Member row (members list) ──────────────────────────────────────────────
function MemberRow({ member, isLeader, onRemove, onSetVice }) {
  const AVATARS = ['🦁', '🐺', '🦊', '🐻', '🐼', '🦅', '🦋', '🐲'];
  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">
      <span className="text-2xl">{AVATARS[member.avatar_index ?? member.avatarIndex ?? 0]}</span>
      <div className="flex-1">
        <p className="text-white text-sm font-medium">{member.display_name ?? member.displayName}
          {(member.role === 'vice_leader' || member.role === 'vice-leader') && (
            <span className="ml-2 text-xs text-amber-400">Vice-Leader</span>
          )}
        </p>
        <p className="text-white/40 text-xs">{Math.round((member.weekly_seconds ?? member.weekSeconds ?? 0) / 3600 * 10) / 10}h this week</p>
      </div>
      {isLeader && (
        <div className="flex gap-2">
          {(member.role !== 'vice_leader' && member.role !== 'vice-leader') && (
            <button
              onClick={() => onSetVice(member.id)}
              title="Make Vice-Leader"
              className="px-2 py-1 rounded text-xs text-amber-300 hover:bg-amber-400/10 transition-colors"
            >
              👑
            </button>
          )}
          <button
            onClick={() => onRemove(member.id)}
            title="Remove member"
            className="px-2 py-1 rounded text-xs text-red-300 hover:bg-red-500/10 transition-colors"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

// ── Contribution row (Patch 3: honour-based pot) ───────────────────────────
function ContributionRow({ contrib, onMarkPaid, onMarkUnpaid }) {
  const AVATARS = ['🦁', '🐺', '🦊', '🐻', '🐼', '🦅', '🦋', '🐲'];
  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-lg"
      style={{ background: 'rgba(255,255,255,0.04)' }}>
      <span className="text-xl">{AVATARS[contrib.avatar_index ?? contrib.avatarIndex ?? 0]}</span>
      <div className="flex-1">
        <p className="text-white text-sm font-medium">{contrib.display_name ?? contrib.displayName}</p>
        <p className="text-white/40 text-xs">₹{contrib.amountRupees}</p>
      </div>
      {/* Patch 3: toggle paid/unpaid manually */}
      <button
        onClick={() => contrib.paid ? onMarkUnpaid(contrib.id) : onMarkPaid(contrib.id)}
        className="px-3 py-1 rounded-full text-xs font-semibold transition-all duration-200"
        style={contrib.paid
          ? { background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)', color: '#86efac' }
          : { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}
      >
        {contrib.paid ? 'PAID ✓' : 'UNPAID'}
      </button>
    </div>
  );
}

export default function LeaderPanel() {
  const toast   = useToast();
  const { user, token } = useAuthStore();

  const API     = import.meta.env.VITE_API_URL ?? '';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // ── State ────────────────────────────────────────────────────────────────
  const [requests,      setRequests]      = useState([]);
  const [members,       setMembers]       = useState([]);
  const [contributions, setContributions] = useState([]);
  const [totalPaise,    setTotalPaise]    = useState(0);
  const [maxPaise,      setMaxPaise]      = useState(15000);
  const [pinnedMsg,     setPinnedMsg]     = useState('');
  const [isSavingPin,   setIsSavingPin]   = useState(false);
  const [confirm,       setConfirm]       = useState(null); // { message, onConfirm }
  const [isLoading,     setIsLoading]     = useState(true);

  // ── Fetch data ───────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    try {
      const [reqRes, membersRes, contrRes] = await Promise.all([
        fetch(`${API}/api/leader/requests`,    { headers }),
        fetch(`${API}/api/groups/me`,           { headers }),
        fetch(`${API}/api/contributions`,       { headers }),
      ]);
      const [rj, mj, cj] = await Promise.all([reqRes.json(), membersRes.json(), contrRes.json()]);

      if (rj.success) setRequests(rj.data.requests ?? []);
      if (mj.success) {
        setMembers(mj.data.members ?? []);
        setPinnedMsg(mj.data.group?.pinnedMessage ?? '');
      }
      if (cj.success) {
        setContributions(cj.data.contributions ?? []);
        setTotalPaise(cj.data.totalPaise ?? 0);
        setMaxPaise(cj.data.maxPaise ?? 15000);
      }
    } finally {
      setIsLoading(false);
    }
  }, [API, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Join request actions ──────────────────────────────────────────────────
  const handleApprove = async (id) => {
    const res  = await fetch(`${API}/api/leader/requests/${id}/approve`, { method: 'POST', headers });
    const json = await res.json();
    if (json.success) { toast.success('Member approved!'); fetchAll(); }
    else toast.error(json.error);
  };

  const handleReject = async (id) => {
    const res  = await fetch(`${API}/api/leader/requests/${id}/reject`, { method: 'POST', headers });
    const json = await res.json();
    if (json.success) { toast.success('Request rejected.'); fetchAll(); }
    else toast.error(json.error);
  };

  // ── Member actions ────────────────────────────────────────────────────────
  const handleRemoveMember = (userId) => {
    setConfirm({
      message: 'Remove this member from your group?',
      onConfirm: async () => {
        setConfirm(null);
        const res  = await fetch(`${API}/api/leader/members/${userId}`, { method: 'DELETE', headers });
        const json = await res.json();
        if (json.success) { toast.success('Member removed.'); fetchAll(); }
        else toast.error(json.error);
      },
    });
  };

  const handleSetVice = async (userId) => {
    const res  = await fetch(`${API}/api/leader/vice`, {
      method: 'PUT', headers, body: JSON.stringify({ userId }),
    });
    const json = await res.json();
    if (json.success) { toast.success('Vice-leader assigned!'); fetchAll(); }
    else toast.error(json.error);
  };

  // ── Pin message ───────────────────────────────────────────────────────────
  const handleSavePin = async () => {
    setIsSavingPin(true);
    const res  = await fetch(`${API}/api/leader/pin`, {
      method: 'PUT', headers, body: JSON.stringify({ message: pinnedMsg || null }),
    });
    const json = await res.json();
    setIsSavingPin(false);
    if (json.success) toast.success('Pinned message updated!');
    else toast.error(json.error);
  };

  // ── Contribution actions (Patch 3: honour-based) ───────────────────────────
  const handleMarkPaid = async (id) => {
    const res  = await fetch(`${API}/api/contributions/${id}/paid`, { method: 'PUT', headers });
    const json = await res.json();
    if (json.success) { toast.success('Marked as paid ✓'); fetchAll(); }
    else toast.error(json.error);
  };

  const handleMarkUnpaid = async (id) => {
    // There is no separate "mark unpaid" endpoint; use reset for the whole week
    // or just refetch and consider it already handled. For now, inform the leader.
    toast.error('To revert a payment, use "Reset Week" to clear all statuses.');
  };

  const handleResetWeek = () => {
    setConfirm({
      message: 'Reset all payment statuses for this week? This cannot be undone.',
      onConfirm: async () => {
        setConfirm(null);
        const res  = await fetch(`${API}/api/contributions/week`, { method: 'DELETE', headers });
        const json = await res.json();
        if (json.success) { toast.success('Week reset — all statuses cleared.'); fetchAll(); }
        else toast.error(json.error);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  const paidCount   = contributions.filter(c => c.paid).length;
  const totalRupees = (totalPaise / 100).toFixed(0);

  return (
    <div className="min-h-screen p-4 md:p-8 max-w-3xl mx-auto space-y-6">
      <ConfirmModal
        isOpen={!!confirm}
        message={confirm?.message ?? ''}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />

      {/* ── Header ── */}
      <div>
        <h1 className="heading text-3xl font-bold text-white">👑 Leader Panel</h1>
        <p className="text-white/40 text-sm mt-1">Manage your group and members</p>
      </div>

      {/* ── Join Requests ── */}
      <section>
        <SectionHead>
          📬 Join Requests
          {requests.length > 0 && (
            <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-indigo-500/30 text-indigo-300">
              {requests.length}
            </span>
          )}
        </SectionHead>
        <div className="glass rounded-xl p-3 space-y-2">
          {requests.length === 0 ? (
            <p className="text-white/30 text-sm text-center py-4">No pending requests</p>
          ) : requests.map(req => (
            <RequestRow key={req.id} req={req} onApprove={handleApprove} onReject={handleReject} />
          ))}
        </div>
      </section>

      {/* ── Members ── */}
      <section>
        <SectionHead>👥 Members ({members.length})</SectionHead>
        <div className="glass rounded-xl p-3 space-y-1">
          {members.map(m => (
            <MemberRow
              key={m.id}
              member={m}
              isLeader={user?.role === 'leader' || user?.role === 'vice-leader' || user?.role === 'vice_leader'}
              onRemove={handleRemoveMember}
              onSetVice={handleSetVice}
            />
          ))}
        </div>
      </section>

      {/* ── Weekly Pot (Patch 3 — honour-based, no Razorpay) ── */}
      <section>
        <SectionHead>💰 Weekly Pot</SectionHead>
        <div className="glass rounded-xl p-5 space-y-4">
          {/* Totals */}
          <div className="flex items-end justify-between">
            <div>
              <p className="text-white/40 text-xs">Total collected</p>
              <p className="text-3xl font-bold text-amber-400">₹{totalRupees}</p>
              <p className="text-white/30 text-xs">of ₹{(maxPaise / 100).toFixed(0)} max</p>
            </div>
            <p className="text-white/50 text-sm">{paidCount}/{contributions.length} paid</p>
          </div>

          {/* Progress bar */}
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div className="h-full rounded-full transition-all duration-500"
              style={{
                width:      `${Math.min((totalPaise / maxPaise) * 100, 100)}%`,
                background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
              }}
            />
          </div>

          {/* Member rows */}
          <div className="space-y-2">
            {contributions.length === 0 ? (
              <p className="text-white/30 text-sm text-center py-2">No contributions set for this week</p>
            ) : contributions.map(c => (
              <ContributionRow
                key={c.id}
                contrib={c}
                onMarkPaid={handleMarkPaid}
                onMarkUnpaid={handleMarkUnpaid}
              />
            ))}
          </div>

          {/* Patch 3: Honour-based note */}
          <p className="text-sm text-white/50 italic">
            💡 Collect payments in cash or UPI. Mark members as paid once received.
          </p>

          {/* Patch 3: Reset Week button */}
          <button
            onClick={handleResetWeek}
            className="w-full py-2 rounded-lg text-sm font-semibold text-amber-300 transition-all duration-200"
            style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)' }}
          >
            🔄 Reset Week
          </button>
        </div>
      </section>

      {/* ── Pinned Message ── */}
      <section>
        <SectionHead>📌 Pinned Message</SectionHead>
        <div className="glass rounded-xl p-5 space-y-3">
          <textarea
            className="input-glass w-full text-sm h-20 resize-none"
            placeholder="Write a pinned message for the group chat…"
            value={pinnedMsg}
            onChange={e => setPinnedMsg(e.target.value)}
            maxLength={300}
            id="leader-pin-message"
          />
          <div className="flex items-center justify-between">
            <p className="text-white/30 text-xs">{pinnedMsg.length}/300</p>
            <button
              onClick={handleSavePin}
              disabled={isSavingPin}
              className="btn-primary px-4 py-1.5 text-sm disabled:opacity-60"
            >
              {isSavingPin ? 'Saving...' : 'Save Pin'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
